import type { AvatarProps } from "@/components/ui/avatar.tsx";
import type { ReactNode } from "react";
import { RoutedLink } from "../components/RoutedLink.tsx";
import { PersonAvatar, type PersonAvatarProps } from "./PersonAvatar.tsx";
import { usePersonProfilePath } from "./usePersonProfilePath.ts";

export interface PersonListItemProps {
    personId?: string;
    displayName: string;
    imageUrl?: string;
    secondaryLabel?: string;
    avatarSize?: AvatarProps["size"];
    avatarIcon?: PersonAvatarProps["placeholderIcon"];
    trailing?: ReactNode;
}

export function PersonListItem({ personId, displayName, imageUrl, secondaryLabel, avatarSize = "lg", avatarIcon, trailing }: PersonListItemProps) {
    const personProfilePath = usePersonProfilePath();

    const surfaceClassName = "hover:bg-secondary rounded-lg";
    const contentClassName = "text-fg hover:no-underline flex min-w-0 items-center gap-3 px-2 py-2";

    const linkContent = (
        <>
            <PersonAvatar person={personId ? { displayName, imageUrl } : undefined} size={avatarSize} className="text-fg" placeholderIcon={avatarIcon} />
            <span className="flex min-w-0 flex-col">
                <span className="text-fg truncate text-sm font-medium">{displayName}</span>
                {secondaryLabel && <span className="text-muted-fg truncate text-xs">{secondaryLabel}</span>}
            </span>
        </>
    );

    const renderRow = (className: string) =>
        personId ? (
            <RoutedLink href={personProfilePath(personId)} className={className}>
                {linkContent}
            </RoutedLink>
        ) : (
            <div className={className}>{linkContent}</div>
        );

    if (!trailing) {
        return renderRow(personId ? `${surfaceClassName} ${contentClassName}` : contentClassName);
    }

    return (
        <div className={`${personId ? `${surfaceClassName} ` : ""}flex items-center gap-1`}>
            {renderRow(`${contentClassName} flex-1`)}
            {trailing}
        </div>
    );
}
